import { Box, Flex, Loader, Stack, Text } from "@mantine/core";
import { IconCamera, IconPlus } from "@tabler/icons-react";
import ImageUploader from "components/ImageUploader";
import ImagePreview from "components/ImageUploader/DocumentPreview";
import IconRoundedCamera from "icons/common/rounded-camera.svg";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentWithId } from "types/document";
import { showError } from "utils/index";

interface FeatureAddPhotosProps {
  chatFeature?: boolean;
  setSelectedImageIndex?: React.Dispatch<React.SetStateAction<number>>;
  imageLimit?: number;
  previewImagesPerRow?: number;
  previewImageSize?: number;
  documents: DocumentWithId[];
  isPending?: boolean;
  required?: boolean;
  onImageUpload: (blob: Blob) => void;
  onRemoveImage: (id: string) => void;
}

const FeatureUploadImages: React.FC<FeatureAddPhotosProps> = ({
  chatFeature = false,
  setSelectedImageIndex,
  imageLimit,
  previewImagesPerRow = 4,
  previewImageSize = 82,
  documents,
  isPending = false,
  required = false,
  onImageUpload,
  onRemoveImage,
}) => {
  const { t } = useTranslation(["discussions", "zod"]);

  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = (blob: Blob | null) => {
    if (!blob) {
      return;
    }
    onImageUpload(blob);
  };

  const uploadingDisabled = imageLimit ? documents.length >= imageLimit : false;
  const hasError = required && documents.length === 0;

  const handleReject = () => {
    if (imageLimit) {
      showError(t("maximumImagesPerMessage", { count: imageLimit }), true);
    }
  };

  return (
    <Box>
      {hasError && (
        <Flex
          gap={"xs"}
          align={"start"}
          className="mb-3 border border-red-alert bg-red-alert/10 text-gray-dark rounded-sm p-2.5"
        >
          <IconCamera className="shrink-0 text-red-alert" />
          <Stack gap={4}>
            <Text fw={600} fz={"sm"} lh={1}>
              {t("zod:errors.imageRequired")}
            </Text>
            <Text fw={400} fz={"sm"} lh={1}>
              {t("zod:errors.imageRequiredDescription")}
            </Text>
          </Stack>
        </Flex>
      )}
      <Box className={chatFeature ? "w-fit mx-auto" : "w-fit"}>
        <Box
          style={{
            display: "grid",
            paddingTop: chatFeature ? "20px" : 0,
            paddingBottom: chatFeature ? "20px" : 0,
            gridTemplateColumns: `repeat(${previewImagesPerRow}, ${previewImageSize}px)`,
            gap: chatFeature ? "20px" : "12px",
            justifyContent: "space-around",
          }}
        >
          <ImageUploader
            grayScale={false}
            w={previewImageSize}
            h={previewImageSize}
            disabled={uploadingDisabled}
            error={hasError}
            onImageUpload={handleImageUpload}
            setIsUploading={setIsUploading}
            maxFiles={imageLimit ? imageLimit - documents.length : undefined}
            onReject={imageLimit ? handleReject : undefined}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: "center",
              }}
            >
              <IconRoundedCamera
                className={`${
                  uploadingDisabled ? "text-gray-light" : "text-gray-medium"
                } w-8 h-7`}
              />
              {!uploadingDisabled && (
                <IconPlus strokeWidth={2} size={18} className="text-green" />
              )}
            </Box>
          </ImageUploader>
          {documents.length !== 0 &&
            documents.map((document, index) => {
              return (
                <ImagePreview
                  onClick={() =>
                    chatFeature &&
                    setSelectedImageIndex &&
                    setSelectedImageIndex(index)
                  }
                  key={document.id}
                  width={previewImageSize}
                  height={previewImageSize}
                  handleRemoveDocument={() => {
                    onRemoveImage(document.id);
                  }}
                  document={document.blob}
                  isPdf={document.blob?.type === "application/pdf"}
                />
              );
            })}

          {(isUploading || isPending) && (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: previewImageSize,
                height: previewImageSize,
                border: "2px solid black",
                borderRadius: 4,
              }}
            >
              <Loader />
            </Box>
          )}
        </Box>
        {uploadingDisabled && (
          <Text c={"var(--color-red-dark)"}>
            * {t("discussions:maximumImagesPerMessage", { count: imageLimit })}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default FeatureUploadImages;
